

import React, { useEffect, useRef, useState } from 'react';
import base from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import { createGroup, updateGroup, getPeriodosEscolares } from '../../api/groups';
import { getAllSubjects } from '../../api/subjects';
import { getAsesores } from '../../api/teachers';
import { getCycles } from '../../api/ciclosEscolares';
import { getInstitutions } from '../../api/institutions';
import { getCampus } from '../../api/campus';
import { getPeriodTypes } from '../../api/periodTypes';
import { getAcademias } from '../../api/academias';
import { getSecciones } from '../../api/secciones';
import { getPrograms } from '../../api/programs';
import { getMateriasPorPrograma } from '../../api/planestudio';

// Opciones para selects (deben ir después de imports)
const estadoGrupoOptions = [
  { value: 'TENTATIVO', label: 'Tentativo' },
  { value: 'ABIERTO', label: 'Abierto' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];
const modalidadOptions = [
  { value: 'PRESENCIAL', label: 'Presencial' },
  { value: 'EN_LINEA', label: 'En línea' },
  { value: 'VIRTUAL_HIBRIDA', label: 'Virtual/Híbrida' },
];

const defaultForm = {
  codigoGrupo: '',
  periodoId: '',
  materiaId: '',
  profesorId: '',
  tipoGrupo: 'ABIERTO',
  estado: 'ABIERTO',
  institucion: '',
  cicloEscolar: '',
  campusId: '',
  academia: '',
  seccionId: '',
  tipoPeriodo: '', // NUEVO: Tipo de periodo (SEMESTRAL, TETRAMESTRAL)
  modalidad: 'PRESENCIAL',
  aula: '',
  bloqueHorario: '',
  horaInicio: '',
  horaFin: '',
  dias: { lunes: false, martes: false, miercoles: false, jueves: false, viernes: false, sabado: false, domingo: false },
  horarioTexto: '',
  horarioJson: null,
  cupoMinimo: 0,
  inscritosActualmente: 0,
  cupoMaximo: 0,
  cupoActual: 0,
  programaAcademicoId: '',
};

export default function NuevoGrupoModal({ open, onClose, onCreated, group = null, mode = 'create', title }) {
  const [form, setForm] = useState(defaultForm);
  const tipoGrupoOptions = [
    { value: 'ABIERTO', label: 'Abierto' },
    { value: 'CERRADO', label: 'Cerrado' },
  ];
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [tiposPeriodo, setTiposPeriodo] = useState([]);
  const [academiaOptions, setAcademiaOptions] = useState([]);
  const [seccionOptions, setSeccionOptions] = useState([]);
  const [programaOptions, setProgramaOptions] = useState([]);
  const [materiasFiltradas, setMateriasFiltradas] = useState([]);
  const [loadingMaterias, setLoadingMaterias] = useState(false);
  const dialogRef = useRef(null);

  // Auto-calcular cupoActual (lugares disponibles) cuando cambien cupoMaximo o inscritosActualmente
  useEffect(() => {
    const max = Number(form.cupoMaximo) || 0;
    const inscritos = Number(form.inscritosActualmente) || 0;
    const disponibles = Math.max(0, max - inscritos);
    setForm(f => {
      if (Number(f.cupoActual) !== disponibles) {
        return { ...f, cupoActual: disponibles };
      }
      return f;
    });
  }, [form.cupoMaximo, form.inscritosActualmente]);

  // Corrige cicloEscolar si el valor es un nombre y los ciclos ya están cargados
  useEffect(() => {
    if (!open || !group || !(mode === 'edit' || mode === 'view')) return;
    if (!form.cicloEscolar || !Array.isArray(ciclos) || ciclos.length === 0) return;
    // Si el valor actual es un nombre, buscar el id
    const esNombre = isNaN(Number(form.cicloEscolar)) && typeof form.cicloEscolar === 'string';
    if (esNombre) {
      const ciclo = ciclos.find(c => c.name === form.cicloEscolar);
      if (ciclo && String(ciclo.id) !== form.cicloEscolar) {
        setForm(f => ({ ...f, cicloEscolar: String(ciclo.id) }));
      }
    }
  }, [ciclos, form.cicloEscolar, open, group, mode]);

  // Sincroniza materiaId cuando las materias del plan de estudios se cargan y hay un grupo en edición
  // Ref para evitar sincronización repetida después de edición manual
  const materiaSyncedRef = useRef(false);
  
  useEffect(() => {
    // Resetear el ref cuando se cierra el modal o cambia el grupo
    if (!open) {
      materiaSyncedRef.current = false;
      return;
    }
    if (!group || !(mode === 'edit' || mode === 'view')) return;
    if (!Array.isArray(materiasFiltradas) || materiasFiltradas.length === 0) return;
    // Si ya se sincronizó, no volver a hacerlo (permite edición manual)
    if (materiaSyncedRef.current) return;
    
    const raw = group._raw || {};
    const materiaIdFromRaw = raw.id_materia;
    
    // Si tenemos un id_materia del backend, verificar que existe en el plan de estudios cargado
    if (materiaIdFromRaw) {
      const materiaExiste = materiasFiltradas.some(m => 
        String(m.id_materia || m.id) === String(materiaIdFromRaw)
      );
      if (materiaExiste) {
        setForm(f => ({ ...f, materiaId: String(materiaIdFromRaw) }));
        materiaSyncedRef.current = true;
      } else {
        console.warn('[NuevoGrupoModal] ⚠️ La materia ID', materiaIdFromRaw, 'no está en el plan de estudios del programa');
      }
    }
  }, [materiasFiltradas, open, group, mode]);

  // Sincroniza profesorId cuando los profesores se cargan y hay un grupo en edición
  // Ref para evitar sincronización repetida después de edición manual
  const profesorSyncedRef = useRef(false);
  
  useEffect(() => {
    // Resetear el ref cuando se cierra el modal o cambia el grupo
    if (!open) {
      profesorSyncedRef.current = false;
      return;
    }
    if (!group || !(mode === 'edit' || mode === 'view')) return;
    if (!Array.isArray(profesores) || profesores.length === 0) return;
    // Si ya se sincronizó, no volver a hacerlo (permite edición manual)
    if (profesorSyncedRef.current) return;
    
    const raw = group._raw || {};
    const asesorIdFromRaw = raw.id_asesor;
    
    console.log('[NuevoGrupoModal] Sincronizando profesorId:', {
      asesorIdFromRaw,
      currentFormProfesorId: form.profesorId,
      profesoresDisponibles: profesores.length
    });
    
    // Si tenemos un id_asesor del backend
    if (asesorIdFromRaw) {
      // Verificar que el asesor existe en las opciones cargadas
      const asesorExiste = profesores.some(p => 
        String(p.id_asesor || p.id) === String(asesorIdFromRaw)
      );
      if (asesorExiste) {
        console.log('[NuevoGrupoModal] ✅ Sincronizando profesorId a:', String(asesorIdFromRaw));
        setForm(f => ({ ...f, profesorId: String(asesorIdFromRaw) }));
        profesorSyncedRef.current = true;
      } else {
        console.warn('[NuevoGrupoModal] ⚠️ El asesor ID', asesorIdFromRaw, 'no existe en las opciones cargadas');
      }
    }
  }, [profesores, open, group, mode]);

  const setField = (name, value) => {
    if (name === 'seccionId') {
      // Al cambiar sección: limpiar programa académico y materia dependientes
      setForm(f => ({ ...f, seccionId: value, programaAcademicoId: '', materiaId: '' }));
      setMateriasFiltradas([]);
    } else if (name === 'programaAcademicoId') {
      // Al cambiar programa: limpiar materia dependiente
      setForm(f => ({ ...f, programaAcademicoId: value, materiaId: '' }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  // Cargar materias del plan de estudios al cambiar el programa académico
  useEffect(() => {
    if (!open) return;
    const programaId = form.programaAcademicoId;
    if (!programaId) {
      setMateriasFiltradas([]);
      return;
    }
    setLoadingMaterias(true);
    getMateriasPorPrograma(programaId)
      .then(data => setMateriasFiltradas(Array.isArray(data) ? data : []))
      .catch(() => setMateriasFiltradas([]))
      .finally(() => setLoadingMaterias(false));
  }, [form.programaAcademicoId, open]);

  // Prefill form for edit/view
  useEffect(() => {
    if (!open) return;
    (async () => {
      try { setPeriodos(await getPeriodosEscolares()); } catch {}
      try { 
        const ciclosData = await getCycles();
        console.log('[NuevoGrupoModal] Ciclos cargados:', ciclosData);
        setCiclos(ciclosData);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando ciclos:', err);
      }
      try { 
        // Usar getAllSubjects para obtener TODAS las materias sin paginación
        const allMaterias = await getAllSubjects();
        console.log('[NuevoGrupoModal] Materias recibidas del API (getAllSubjects):', allMaterias?.length);
        // CRÍTICO: Solo usar materias del backend (con id_materia numérico)
        // Las materias con ID "local-*" están en localStorage pero NO en la base de datos
        const materiasBackend = (Array.isArray(allMaterias) ? allMaterias : []).filter(m => {
          const id = m.id_materia || m.id;
          const esNumerico = typeof id === 'number';
          if (!esNumerico) {
            console.warn('[NuevoGrupoModal] ⚠️ Materia descartada (NO está en backend):', m.nombre, 'ID:', id);
          }
          return esNumerico;
        });
        console.log('[NuevoGrupoModal] Materias del backend (válidas para grupos):', materiasBackend.length);
        if (materiasBackend.length === 0) {
          console.error('[NuevoGrupoModal] ❌ No hay materias en el backend. Crea materias desde la pantalla de Materias.');
        }
        setMaterias(materiasBackend);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando materias:', err);
        setMaterias([]);
      }
      try { 
        const asesores = await getAsesores();
        console.log('[NuevoGrupoModal] ===== ASESORES RECIBIDOS =====');
        console.log('[NuevoGrupoModal] Total asesores:', asesores?.length);
        console.log('[NuevoGrupoModal] Asesores completos:', JSON.stringify(asesores, null, 2));
        // Excluir asesores de tipo Administrativo: solo pueden impartir grupos
        // quienes tengan tipo ASESOR o ASESOR_ADMINISTRATIVO
        const docentes = Array.isArray(asesores)
          ? asesores.filter(a => a.advisorType !== 'ADMINISTRATIVO' && a.tipo_asesor !== 'ADMINISTRATIVO')
          : [];
        setProfesores(docentes);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando asesores:', err);
        setProfesores([]);
      }
      try {
        const insts = await getInstitutions();
        console.log('[NuevoGrupoModal] Instituciones cargadas:', insts?.length);
        setInstituciones(Array.isArray(insts) ? insts : []);
        if (insts.length > 0 && !form.institucion) {
          setField('institucion', insts[0].id);
        }
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando instituciones:', err);
      }
      try {
        const camps = await getCampus();
        console.log('[NuevoGrupoModal] Campus cargados:', camps?.length);
        const campusOpts = (Array.isArray(camps) ? camps : []).map(c => ({
          value: c.id,
          label: c.nombre || c.codigo
        }));
        setCampusOptions(campusOpts);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando campus:', err);
      }
      try {
        const tipos = await getPeriodTypes();
        console.log('[NuevoGrupoModal] ===== TIPOS PERIODO RECIBIDOS =====');
        console.log('[NuevoGrupoModal] Total tipos:', tipos?.length);
        console.log('[NuevoGrupoModal] Tipos completos:', JSON.stringify(tipos, null, 2));
        setTiposPeriodo(Array.isArray(tipos) ? tipos : []);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando tipos de periodo:', err);
        setTiposPeriodo([]);
      }
      try {
        const acads = await getAcademias();
        console.log('[NuevoGrupoModal] Academias cargadas:', acads?.length);
        const acadOpts = (Array.isArray(acads) ? acads : []).map(a => ({
          value: a.id,
          label: a.name || a.nombre
        }));
        setAcademiaOptions(acadOpts);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando academias:', err);
        setAcademiaOptions([]);
      }
      try {
        const secciones = await getSecciones();
        console.log('[NuevoGrupoModal] Secciones cargadas:', secciones?.length);
        const secOpts = (Array.isArray(secciones) ? secciones : []).map(s => ({
          value: s.id,
          label: s.nombre,
        }));
        setSeccionOptions(secOpts);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando secciones académicas:', err);
        setSeccionOptions([]);
      }
      try {
        const progs = await getPrograms();
        console.log('[NuevoGrupoModal] Programas académicos cargados:', progs?.length);
        const progOpts = (Array.isArray(progs) ? progs : []).map(p => ({
          value: p.id,
          label: p.name || p.nombre_programa || `Programa ${p.id}`,
          sectionId: p.sectionId ?? null,
        }));
        setProgramaOptions(progOpts);
      } catch (err) {
        console.error('[NuevoGrupoModal] Error cargando programas académicos:', err);
        setProgramaOptions([]);
      }
    })();
    if (group && (mode === 'edit' || mode === 'view')) {
      // Usar _raw para obtener los IDs reales del backend
      const raw = group._raw || {};
      console.log('[NuevoGrupoModal] ===== CARGANDO DATOS PARA EDICIÓN =====');
      console.log('[NuevoGrupoModal] Group completo:', group);
      console.log('[NuevoGrupoModal] _raw del grupo:', raw);
      console.log('[NuevoGrupoModal] id_materia en _raw:', raw.id_materia, 'tipo:', typeof raw.id_materia);
      console.log('[NuevoGrupoModal] id_asesor en _raw:', raw.id_asesor, 'tipo:', typeof raw.id_asesor);
      console.log('[NuevoGrupoModal] id_seccion en _raw:', raw.id_seccion, 'tipo:', typeof raw.id_seccion);
      console.log('[NuevoGrupoModal] id_academia en _raw:', raw.id_academia, 'tipo:', typeof raw.id_academia);
      let cicloEscolarId = '';
      if (raw.id_ciclo_escolar) {
        cicloEscolarId = String(raw.id_ciclo_escolar);
      } else if (typeof group.cicloEscolar === 'number') {
        cicloEscolarId = String(group.cicloEscolar);
      } else if (typeof group.cicloEscolar === 'string' && Array.isArray(ciclos) && ciclos.length > 0) {
        // Buscar el id correspondiente al nombre
        const ciclo = ciclos.find(c => c.name === group.cicloEscolar);
        if (ciclo) cicloEscolarId = String(ciclo.id);
      }
      // Extraer datos de horario del _raw si existen
      let bloqueHorario = raw.turno || '';
      let horaInicio = '';
      let horaFin = '';
      let dias = { lunes: false, martes: false, miercoles: false, jueves: false, viernes: false, sabado: false, domingo: false };
      let horarioTexto = '';
      if (raw.horario && typeof raw.horario === 'object') {
        if (!bloqueHorario) bloqueHorario = raw.horario.bloque || '';
        horaInicio = raw.horario.hora_inicio || '';
        horaFin = raw.horario.hora_fin || '';
        if (Array.isArray(raw.horario.dias)) {
          raw.horario.dias.forEach(d => {
            if (dias.hasOwnProperty(d)) dias[d] = true;
          });
        }
        if (raw.horario.detalle_texto) horarioTexto = raw.horario.detalle_texto;
      } else if (raw.horario && typeof raw.horario === 'string') {
        horarioTexto = raw.horario;
      }

      setForm(() => ({
        ...defaultForm,
        codigoGrupo: raw.codigo_grupo || group.codigo_grupo || '',
        materiaId: raw.id_materia ? String(raw.id_materia) : '',
        profesorId: raw.id_asesor ? String(raw.id_asesor) : '',
        cicloEscolar: cicloEscolarId,
        campusId: raw.id_campus ? String(raw.id_campus) : '',
        seccionId: raw.id_seccion ? String(raw.id_seccion) : '',
        academia: raw.id_academia ? String(raw.id_academia) : '',
        periodoId: raw.periodo_numero ? String(raw.periodo_numero) : '',
        tipoPeriodo: raw.tipo_periodo ? String(raw.tipo_periodo) : '', // NUEVO: Cargar tipo_periodo desde raw
        tipoGrupo: raw.tipo_grupo || 'ABIERTO',
        estado: raw.estado_grupo || 'ABIERTO',
        modalidad: raw.modalidad || 'PRESENCIAL',
        cupoMaximo: raw.cupo_maximo || 0,
        cupoMinimo: raw.cupo_minimo || 0,
        inscritosActualmente: raw.inscritos_actuales || 0,
        aula: raw.aula || '',
        institucion: raw.id_institucion ? String(raw.id_institucion) : '',
        academia: raw.id_academia ? String(raw.id_academia) : '',
        programaAcademicoId: raw.id_programa_academico ? String(raw.id_programa_academico) : '',
        bloqueHorario,
        horaInicio,
        horaFin,
        dias,
        horarioTexto,
      }));
    } else {
      setForm(defaultForm);
    }
    setErrors({});
    setActiveTab('general');
    setTimeout(() => {
      const first = dialogRef.current?.querySelector('input, select, textarea');
      first?.focus();
    }, 100);
  }, [open, group, mode]);

  if (!open) return null;

  // Opciones para dropdown de Ciclo Escolar (requerido para id_ciclo_escolar)
  let cicloOptions = Array.isArray(ciclos) ? ciclos.map(c => ({ 
    value: c.id, 
    label: c.name || c.nombre || `Ciclo ${c.id}`
  })) : [];
  console.log('[NuevoGrupoModal] cicloOptions generadas:', cicloOptions);
  console.log('[NuevoGrupoModal] ciclos RAW:', ciclos);
  
  // Opciones para dropdown de Tipo de Periodo (opcional)
  let periodoOptions = Array.isArray(tiposPeriodo) ? tiposPeriodo.map(p => ({ 
    value: p.id_tipo_periodo || p.id, 
    label: p.nombre_periodo || p.nombre 
  })) : [];
  console.log('[NuevoGrupoModal] periodoOptions generadas:', periodoOptions);
  
  // Opciones de materias derivadas del plan de estudios del programa seleccionado
  const materiaOptions = materiasFiltradas.map(m => ({
    value: String(m.id_materia || m.id),
    label: m.nombre_materia || m.nombre || m.name
  }));

  // Programas filtrados según la sección académica seleccionada
  const programaOptionsFiltradas = programaOptions.filter(o =>
    !form.seccionId || o.sectionId === null || String(o.sectionId) === String(form.seccionId)
  );
  
  const profesorOptions = Array.isArray(profesores) ? profesores.map(a => { 
    const id = a.id_asesor || a.id;
    const nombre = a.nombre_completo || a.nombre || a.name || `Asesor ${id}`;
    return { value: String(id), label: nombre }; // Convertir a string para consistencia
  }) : [];
  console.log('[NuevoGrupoModal] profesorOptions generadas:', profesorOptions);

  const validate = () => {
    const e = {};
    if (!form.codigoGrupo.trim()) e.codigoGrupo = 'Requerido';
    if (!form.institucion) e.institucion = 'Selecciona una institución';
    if (!form.cicloEscolar) e.cicloEscolar = 'Selecciona un ciclo escolar';
    // tipoPeriodo es REQUERIDO para crear, pero puede venir vacío al editar si ya tiene valor en BD
    if (mode === 'create' && !form.tipoPeriodo) e.tipoPeriodo = 'Selecciona tipo de período (Semestral/Tetramestral)';
    if (!form.materiaId) e.materiaId = 'Selecciona una materia';
    if (!form.profesorId) e.profesorId = 'Selecciona un docente';
    if (!form.campusId) e.campusId = 'Selecciona un campus';
    if (!form.seccionId) e.seccionId = 'Selecciona una sección';
    if (!form.tipoGrupo) e.tipoGrupo = 'Selecciona tipo de grupo';
    if (!form.estado) e.estado = 'Selecciona un estado';
    if (!form.modalidad) e.modalidad = 'Selecciona una modalidad';
    if (Number(form.cupoMinimo) > Number(form.cupoMaximo) && Number(form.cupoMaximo) > 0) e.cupoMinimo = 'Mínimo no puede exceder máximo';
    if (Number(form.inscritosActualmente) > Number(form.cupoMaximo) && Number(form.cupoMaximo) > 0) e.inscritosActualmente = 'Inscritos excede cupo máximo';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const parseHorario = () => {
    const txt = form.horarioTexto.trim();
    // Construir estructura combinada si se proporcionan campos estructurados
    const diasSeleccionados = Object.entries(form.dias).filter(([, v]) => v).map(([k]) => k);
    const tieneEstructurado = diasSeleccionados.length || form.horaInicio || form.horaFin || form.bloqueHorario;
    let estructurado = null;
    if (tieneEstructurado) {
      estructurado = {
        bloque: form.bloqueHorario || null,
        dias: diasSeleccionados,
        hora_inicio: form.horaInicio || null,
        hora_fin: form.horaFin || null,
      };
    }
    if (!txt && !estructurado) return null;
    if (txt) {
      try { const parsed = JSON.parse(txt); return estructurado ? { ...estructurado, detalle: parsed } : parsed; } catch { return estructurado ? { ...estructurado, detalle_texto: txt } : txt; }
    }
    return estructurado;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Log del estado completo del formulario
    console.log('[NuevoGrupoModal] ===== INICIO SUBMIT =====');
    console.log('[NuevoGrupoModal] Form completo:', JSON.stringify(form, null, 2));
    console.log('[NuevoGrupoModal] Materias disponibles:', materias.length, materias.map(m => ({
      id: m.id_materia || m.id,
      nombre: m.nombre_materia || m.nombre
    })));
    console.log('[NuevoGrupoModal] Profesores disponibles:', profesores.length, profesores.map(p => ({
      id: p.id_asesor || p.id,
      nombre: p.nombre_completo || p.nombre
    })));
    
    if (!validate()) {
      console.log('[NuevoGrupoModal] Validación fallida, errores:', errors);
      return;
    }
    // Validar que materia y asesor existan en el catálogo
    const materiaIds = materiasFiltradas.map(m => String(m.id_materia || m.id));
    const asesorIds = profesores.map(a => String(a.id_asesor || a.id));
    
    if (!form.materiaId || !materiaIds.includes(String(form.materiaId))) {
      console.error('[NuevoGrupoModal] ❌ Materia inválida o vacía');
      setErrors(er => ({ ...er, materiaId: 'Selecciona una materia válida del programa' }));
      return;
    }
    
    console.log('[NuevoGrupoModal] Validando asesor:', {
      formProfesorId: form.profesorId,
      tipo: typeof form.profesorId,
      asesorIdsDisponibles: asesorIds,
      existe: asesorIds.includes(String(form.profesorId))
    });
    
    if (form.profesorId && !asesorIds.includes(String(form.profesorId))) {
      console.error('[NuevoGrupoModal] Asesor inválido');
      setErrors(er => ({ ...er, profesorId: 'Selecciona un asesor válido' }));
      return;
    }
    setSaving(true);
    try {
      // Asegura que cicloEscolar sea el id (número) antes de enviar
      let cicloEscolarId = form.cicloEscolar;
      if (isNaN(Number(cicloEscolarId)) && typeof cicloEscolarId === 'string' && Array.isArray(ciclos) && ciclos.length > 0) {
        const ciclo = ciclos.find(c => c.name === cicloEscolarId);
        if (ciclo) cicloEscolarId = String(ciclo.id);
      }
      // El mapeo final se hace en groups.js, aquí solo aseguramos que los valores sean válidos
      const payloadForm = {
        ...form,
        cicloEscolar: cicloEscolarId,
        horarioJson: parseHorario(),
        tipoGrupo: form.tipoGrupo,
        modalidad: form.modalidad,
        estado: form.estado,
      };
      console.log('[NuevoGrupoModal] 📤 Payload a enviar:', JSON.stringify(payloadForm, null, 2));
      if (mode === 'edit' && group) {
        await updateGroup(group.id, payloadForm);
      } else {
        await createGroup(payloadForm);
      }
      console.log('[NuevoGrupoModal] ✅ Grupo guardado exitosamente');
      onCreated?.();
      onClose?.();
    } catch (err) {
      console.error('[NuevoGrupoModal] ❌ Error al guardar grupo:', err);
      setErrors(er => ({ ...er, global: err.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={base.modalBackdrop} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className={base.modalContainer} role="dialog" aria-modal="true" aria-labelledby="grupo-title" ref={dialogRef}>
        <div className={base.modalHeader}>
          <h2 id="grupo-title" className={base.modalTitle}>{mode === 'edit' ? 'Editar Grupo' : mode === 'view' ? 'Ver Grupo' : (title || 'Nuevo Grupo')}</h2>
          <button type="button" className={base.modalClose} aria-label="Cerrar" onClick={onClose}>✕</button>
        </div>
        <form className={base.modalBody} onSubmit={handleSubmit}>
          <div className={base.tabsRow} style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button type="button" className={`${base.tabButton} ${activeTab === 'general' ? base.tabButtonActive : ''}`} onClick={() => setActiveTab('general')}>Información General</button>
            <button type="button" className={`${base.tabButton} ${activeTab === 'horario' ? base.tabButtonActive : ''}`} onClick={() => setActiveTab('horario')}>Horario</button>
            <button type="button" className={`${base.tabButton} ${activeTab === 'cupos' ? base.tabButtonActive : ''}`} onClick={() => setActiveTab('cupos')}>Cupos</button>
          </div>
          {errors.global && (
            <div style={{ 
              backgroundColor: '#ffebee', 
              border: '1px solid #f44336', 
              borderRadius: '8px', 
              padding: '12px 16px', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <span style={{ color: '#f44336', fontSize: '20px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#c62828', display: 'block', marginBottom: '4px' }}>Error al guardar</strong>
                <span style={{ color: '#c62828', fontSize: '14px', whiteSpace: 'pre-line' }}>{errors.global}</span>
              </div>
              <button 
                type="button" 
                onClick={() => setErrors(e => ({ ...e, global: undefined }))}
                style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '16px' }}
              >✕</button>
            </div>
          )}
          {activeTab === 'general' && (
            <div className={base.modalGrid}>
              <div className={base.formGroup}>
                <label className={base.label}>Institución *</label>
                <select className={base.select} value={form.institucion} onChange={e => setField('institucion', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona institución —</option>
                  {instituciones.map(inst => (
                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                  ))}
                </select>
                {errors.institucion && <span className={base.errorText}>{errors.institucion}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>ID Grupo *</label>
                <input className={base.input} value={form.codigoGrupo} onChange={e => setField('codigoGrupo', e.target.value)} disabled={mode !== 'create'} />
                {errors.codigoGrupo && <span className={base.errorText}>{errors.codigoGrupo}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Ciclo Escolar *</label>
                <select
                  className={base.select}
                  value={form.cicloEscolar}
                  onChange={e => {
                    // Siempre guardar el id como string
                    const selectedId = String(e.target.value);
                    setField('cicloEscolar', selectedId);
                  }}
                  disabled={mode === 'view' || ciclos.length === 0}
                >
                  <option value="">— Selecciona ciclo —</option>
                  {ciclos.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.cicloEscolar && <span className={base.errorText}>{errors.cicloEscolar}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Tipo de Período *</label>
                <select className={base.select} value={form.tipoPeriodo} onChange={e => setField('tipoPeriodo', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona tipo de período —</option>
                  <option value="SEMESTRAL">Semestral</option>
                  <option value="TETRAMESTRAL">Tetramestral</option>
                </select>
                {errors.tipoPeriodo && <span className={base.errorText}>{errors.tipoPeriodo}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Tipo de grupo *</label>
                <select className={base.select} value={form.tipoGrupo} onChange={e => setField('tipoGrupo', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona tipo —</option>
                  {tipoGrupoOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {errors.tipoGrupo && <span className={base.errorText}>{errors.tipoGrupo}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Estado *</label>
                <select className={base.select} value={form.estado} onChange={e => setField('estado', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona estado —</option>
                  {estadoGrupoOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {errors.estado && <span className={base.errorText}>{errors.estado}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Campus *</label>
                <select className={base.select} value={form.campusId} onChange={e => setField('campusId', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona campus —</option>
                  {campusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {errors.campusId && <span className={base.errorText}>{errors.campusId}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Sección Académica *</label>
                <select
                  className={base.select}
                  value={form.seccionId}
                  onChange={e => setField('seccionId', e.target.value)}
                  disabled={mode === 'view'}
                >
                  <option value="">— Selecciona sección —</option>
                  {seccionOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {errors.seccionId && <span className={base.errorText}>{errors.seccionId}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Academia</label>
                <select className={base.select} value={form.academia} onChange={e => setField('academia', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona academia —</option>
                  {academiaOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Programa Académico</label>
                <select className={base.select} value={form.programaAcademicoId} onChange={e => setField('programaAcademicoId', e.target.value)} disabled={mode === 'view' || !form.seccionId}>
                  <option value="">{!form.seccionId ? '— Selecciona una sección primero —' : '— Selecciona programa —'}</option>
                  {programaOptionsFiltradas.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Docente Asignado *</label>
                <select 
                  className={base.select} 
                  value={form.profesorId} 
                  onChange={e => {
                    console.log('[NuevoGrupoModal] Cambiando asesor a:', e.target.value);
                    setField('profesorId', e.target.value);
                  }} 
                  disabled={mode === 'view'}
                >
                  <option value="">— Selecciona docente —</option>
                  {profesorOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {errors.profesorId && <span className={base.errorText}>{errors.profesorId}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Materia *</label>
                <select className={base.select} value={form.materiaId} onChange={e => setField('materiaId', e.target.value)} disabled={mode === 'view' || !form.programaAcademicoId}>
                  <option value="">{!form.seccionId ? '— Selecciona una sección primero —' : !form.programaAcademicoId ? '— Selecciona un programa primero —' : loadingMaterias ? 'Cargando materias...' : '— Selecciona materia —'}</option>
                  {materiaOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {errors.materiaId && <span className={base.errorText}>{errors.materiaId}</span>}
              </div>
            </div>
          )}
          {activeTab === 'horario' && (
            <div className={base.modalGrid}>
              <div className={base.formGroup}>
                <label className={base.label}>Modalidad *</label>
                <select className={base.select} value={form.modalidad} onChange={e => setField('modalidad', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona modalidad —</option>
                  {modalidadOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {errors.modalidad && <span className={base.errorText}>{errors.modalidad}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Horario</label>
                <select className={base.select} value={form.bloqueHorario} onChange={e => setField('bloqueHorario', e.target.value)} disabled={mode === 'view'}>
                  <option value="">— Selecciona —</option>
                  <option value="Matutino">Matutino</option>
                  <option value="Nocturno">Nocturno</option>
                  <option value="Sabatino">Sabatino</option>
                  <option value="En-Línea">En-Línea</option>
                  <option value="Virtual">Virtual</option>
                </select>
              </div>
              <div className={base.formGroup} style={{ gridColumn: 'span 2' }}>
                <label className={base.label}>Frecuencia de clase</label>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'12px' }}>
                    {[
                      ['lunes','Lunes'],['martes','Martes'],['miercoles','Miércoles'],['jueves','Jueves'],['viernes','Viernes'],['sabado','Sábado'],['domingo','Domingo']
                    ].map(([key,label]) => (
                      <label key={key} style={{ display:'flex', alignItems:'center', gap:'4px', font:'400 13px Roboto, sans-serif' }}>
                        <input
                          type="checkbox"
                          checked={form.dias[key]}
                          onChange={e => setField('dias', { ...form.dias, [key]: e.target.checked })}
                          disabled={mode === 'view'}
                          style={{ accentColor: 'var(--Azul, #002f6c)' }}
                        />{label}
                      </label>
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:'24px', flexWrap:'wrap' }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                      <span style={{ font:'800 12px Roboto, sans-serif', color:'var(--Azul,#002f6c)' }}>Hora Inicio:</span>
                      <input type="time" className={base.input} value={form.horaInicio} onChange={e => setField('horaInicio', e.target.value)} style={{ width:'130px', padding:'6px 8px' }} />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                      <span style={{ font:'800 12px Roboto, sans-serif', color:'var(--Azul,#002f6c)' }}>Hora Fin:</span>
                      <input type="time" className={base.input} value={form.horaFin} onChange={e => setField('horaFin', e.target.value)} style={{ width:'130px', padding:'6px 8px' }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Aula/Plataforma</label>
                <input
                  type="text"
                  className={base.input}
                  value={form.aula}
                  onChange={e => setField('aula', e.target.value)}
                  placeholder="Ej: Aula-01"
                  maxLength={50}
                  disabled={mode === 'view'}
                />
              </div>
              <div className={base.formGroup} style={{ gridColumn: 'span 2' }}>
                <label className={base.label}>Detalle adicional</label>
                <textarea className={base.textarea} rows={4} value={form.horarioTexto} onChange={e => setField('horarioTexto', e.target.value)} placeholder='Información adicional del horario' disabled={mode === 'view'} />
              </div>
            </div>
          )}
          {activeTab === 'cupos' && (
            <div className={base.modalGrid}>
              <div className={base.formGroup}>
                <label className={base.label}>Cupo Mínimo</label>
                <input type="number" min="0" className={base.input} value={form.cupoMinimo} onChange={e => setField('cupoMinimo', e.target.value)} disabled={mode === 'view'} />
                {errors.cupoMinimo && <span className={base.errorText}>{errors.cupoMinimo}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Cupo Máximo</label>
                <input type="number" min="0" className={base.input} value={form.cupoMaximo} onChange={e => setField('cupoMaximo', e.target.value)} disabled={mode === 'view'} />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Inscritos Actualmente</label>
                <input type="number" min="0" className={base.input} value={form.inscritosActualmente} onChange={e => setField('inscritosActualmente', e.target.value)} disabled={mode === 'view'} />
                {errors.inscritosActualmente && <span className={base.errorText}>{errors.inscritosActualmente}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Cupo Disponible</label>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <div
                    className={base.progressBarWrapper}
                    role="progressbar"
                    aria-valuenow={Number(form.cupoMaximo) > 0 ? (Math.min(Number(form.inscritosActualmente), Number(form.cupoMaximo)) / Number(form.cupoMaximo)) * 100 : 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{ flex: '1 1 auto' }}
                  >
                    <div
                      className={base.progressBarFill}
                      style={{ width: (Number(form.cupoMaximo) > 0 ? (Math.min(Number(form.inscritosActualmente), Number(form.cupoMaximo)) / Number(form.cupoMaximo)) * 100 : 0) + '%'}}
                    >
                      <span className={base.progressBarText}>
                        {Number(form.cupoMaximo) > 0 ? ((Math.min(Number(form.inscritosActualmente), Number(form.cupoMaximo)) / Number(form.cupoMaximo)) * 100).toFixed(1) + '%' : '0%'}
                      </span>
                    </div>
                  </div>
                  <input type="number" className={base.input} value={form.cupoActual} style={{ width:'90px' }} disabled title="Calculado automáticamente: Cupo Máximo - Inscritos" />
                </div>
              </div>
            </div>
          )}
          <div className={base.modalActions}>
            {mode !== 'view' && (
              <button type="submit" disabled={saving} className={base.primaryButton}>{saving ? 'GUARDANDO...' : 'GUARDAR'}</button>
            )}
            <button type="button" className={base.secondaryButton} onClick={onClose}>CERRAR</button>
          </div>
        </form>
      </div>
    </div>
  );
}
